import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Package, MapPin, CheckCircle, Truck, CreditCard } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { parcelsApi } from '../lib/api';
import { formatDate, getStatusLabel, getStatusColor } from '../lib/utils';

const TrackingPage = () => {
  const { id } = useParams();
  const [parcel, setParcel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchParcel = useCallback(async () => {
    try {
      const response = await parcelsApi.track(id);
      setParcel(response.data);
    } catch (err) {
      setError('Colis non trouvé');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchParcel();
  }, [fetchParcel]);

  const statusSteps = [
    { key: 'created', label: 'Créée', icon: Package },
    { key: 'accepted', label: 'Acceptée', icon: CheckCircle },
    { key: 'in_progress', label: 'En cours', icon: Truck },
    { key: 'delivered', label: 'Livrée', icon: MapPin },
    { key: 'paid', label: 'Payée', icon: CreditCard },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="border-slate-200 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase mb-2">
              Colis non trouvé
            </h1>
            <p className="text-slate-600">
              Vérifiez le numéro de suivi et réessayez.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStepIndex = statusSteps.findIndex(s => s.key === parcel.status);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="tracking-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Package className="w-8 h-8 text-orange-500" />
            <span className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900">COLISFLASH</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase">
            Suivi de colis
          </h1>
          <p className="text-slate-500 mt-2">ID: {id.slice(0, 8)}...</p>
        </div>

        {/* Status */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(parcel.status)}`}>
                {getStatusLabel(parcel.status)}
              </span>
            </div>
            
            <div className="flex items-center justify-between overflow-x-auto pb-4">
              {statusSteps.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                return (
                  <div key={step.key} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        isCompleted 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-100 text-slate-400'
                      } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-xs mt-2 font-medium whitespace-nowrap ${isCompleted ? 'text-green-600' : 'text-slate-400'}`}>
                        {step.label}
                      </span>
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div className={`w-8 md:w-16 h-1 mx-1 ${
                        index < currentStepIndex ? 'bg-green-500' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Route */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div className="w-0.5 h-12 bg-slate-200 my-2" />
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase">Départ</p>
                  <p className="font-medium text-slate-900">{parcel.pickup_address.city}</p>
                  <p className="text-sm text-slate-500">{parcel.pickup_address.postal_code}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Arrivée</p>
                  <p className="font-medium text-slate-900">{parcel.delivery_address.city}</p>
                  <p className="text-sm text-slate-500">{parcel.delivery_address.postal_code}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{parcel.distance_km} km</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase mb-4">
              Détails
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Poids</p>
                <p className="font-medium text-slate-900">{parcel.weight} kg</p>
              </div>
              <div>
                <p className="text-slate-500">Dimensions</p>
                <p className="font-medium text-slate-900">
                  {parcel.dimensions.length}x{parcel.dimensions.width}x{parcel.dimensions.height} cm
                </p>
              </div>
              <div>
                <p className="text-slate-500">Date d'enlèvement</p>
                <p className="font-medium text-slate-900">{formatDate(parcel.pickup_date)}</p>
              </div>
              <div>
                <p className="text-slate-500">Créé le</p>
                <p className="font-medium text-slate-900">{formatDate(parcel.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrackingPage;
